import { ReccobeatsAudioFeature } from '../types';

/**
 * Find the closest track to a given point in valence-energy space using euclidean distance
 * @param targetValence Target valence value (0 to 1)
 * @param targetEnergy Target energy value (0 to 1)
 * @param audioFeatures Array of audio features with valence and energy
 * @returns The audio feature of the closest track, or null if no tracks available
 */
export const findClosestTrack = (
  targetValence: number,
  targetEnergy: number,
  audioFeatures: ReccobeatsAudioFeature[]
): ReccobeatsAudioFeature | null => {
  if (!audioFeatures || audioFeatures.length === 0) {
    return null;
  }

  let closestTrack: ReccobeatsAudioFeature | null = null;
  let minDistance = Infinity;

  for (const feature of audioFeatures) {
    // Calculate euclidean distance: sqrt((v1-v2)² + (e1-e2)²)
    const valenceDiff = feature.valence - targetValence;
    const energyDiff = feature.energy - targetEnergy;
    const distance = Math.sqrt(valenceDiff * valenceDiff + energyDiff * energyDiff);

    if (distance < minDistance) {
      minDistance = distance;
      closestTrack = feature;
    }
  }

  return closestTrack;
};

/**
 * Validate recommendation count is within acceptable bounds
 * @param count Number of recommendations requested
 * @returns True if valid, false otherwise
 */
export const validateRecommendationCount = (count: number): boolean => {
  return Number.isInteger(count) && count >= 1 && count <= 100;
};

/**
 * Format track IDs as Spotify URIs
 * @param trackIds Array of Spotify track IDs
 * @returns Array of Spotify track URIs in format "spotify:track:{id}"
 */
export const formatTrackUris = (trackIds: string[]): string[] => {
  return trackIds.map(id => `spotify:track:${id}`);
};

/**
 * Convert heatmap coordinates from [-1, 1] range to [0, 1] range
 * @param value Value in [-1, 1] range
 * @returns Value in [0, 1] range
 */
export const normalizeCoordinate = (value: number): number => {
  return (value + 1) / 2;
};
